import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Navigate, Link } from 'react-router-dom'
import { setAuth } from '../../redux/authSlice'
import { RootState } from '../../redux/store'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import './login.css'

const Login: React.FC = () => {
  const [email, setEmail]             = useState('')
  const [password, setPassword]       = useState('')
  const [showPass, setShowPass]       = useState(false)
  const [error, setError]             = useState<string | null>(null)
  const [emailErr, setEmailErr]       = useState(false)
  const [passErr, setPassErr]         = useState(false)
  const [rememberMe, setRememberMe]   = useState(false)
  const [loading, setLoading]         = useState(false)

  const auth     = useSelector((state: RootState) => state.auth.value)
  const dispatch = useDispatch()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get('user')
        if (data) dispatch(setAuth(true))
      } catch {
        // auth errors handled by global interceptor
      }
    }
    checkAuth()
  }, [dispatch])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setEmailErr(false)
    setPassErr(false)

    let valid = true
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailErr(true); valid = false }
    if (!password) { setPassErr(true); valid = false }
    if (!valid) return

    setLoading(true)
    try {
      const { data } = await axios.post('login', { email, password, rememberMe }, { withCredentials: true })
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`
      localStorage.setItem('authToken', data.token)
      dispatch(setAuth(true))
    } catch {
      setError('Invalid email or password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (auth) return <Navigate to='/' />

  return (
    <div className='lp-root'>
      <div className='lp-mesh' />
      <div className='lp-mesh-noise' />

      <div className='lp-page'>

        {/* ══ LEFT PANEL ══ */}
        <div className='lp-left'>
          <div className='lp-left-content'>

            <div className='lp-brand'>
              <div className='lp-brand-mark'>T</div>
              <div>
                <div className='lp-brand-name'>TimeBerlo.com</div>
                <div className='lp-brand-tag'>Portal</div>
              </div>
            </div>

            <div className='lp-hero-title'>
              The smarter way to<br />
              <span className='lp-accent'>manage your team</span>
            </div>
            <div className='lp-hero-sub'>
              Timesheets, approvals, expenses, HR documents and CRM — all in one place. Sign in to access your portal.
            </div>

            {/* Dashboard mockup */}
            <div className='lp-mockup-wrap'>
              <div className='lp-mockup'>
                <div className='lp-mockup-bar'>
                  <div className='lp-mock-dot' style={{ background: '#ef4444' }} />
                  <div className='lp-mock-dot' style={{ background: '#f59e0b' }} />
                  <div className='lp-mock-dot' style={{ background: '#10b981' }} />
                  <div style={{ flex: 1, marginLeft: 10, height: 16, background: 'var(--lp-brd)', borderRadius: 4, maxWidth: 160, opacity: .5 }} />
                </div>
                <div className='lp-mockup-body'>
                  <div className='lp-mock-row'>
                    {[
                      { label: 'TOIL Balance', val: '+7.5', unit: 'h', color: '#2563eb', w: '62%', grad: '#2563eb,#60a5fa' },
                      { label: 'Approvals',    val: '4',    unit: ' pend.', color: '#d97706', w: '40%', grad: '#d97706,#fbbf24' },
                      { label: 'Logged hrs',   val: '38',   unit: '/40', color: '#059669', w: '95%', grad: '#059669,#34d399' },
                    ].map(s => (
                      <div className='lp-mock-stat' key={s.label}>
                        <div className='lp-stat-label'>{s.label}</div>
                        <div className='lp-stat-val' style={{ color: s.color }}>{s.val}<span className='lp-stat-unit'>{s.unit}</span></div>
                        <div className='lp-bar-wrap'><div className='lp-bar-fill' style={{ width: s.w, background: `linear-gradient(90deg,${s.grad})` }} /></div>
                      </div>
                    ))}
                  </div>
                  <div className='lp-mock-table'>
                    <div className='lp-mock-th'>
                      <div className='lp-mock-th-c'>Employee</div>
                      <div className='lp-mock-th-c'>Week</div>
                      <div className='lp-mock-th-c'>Status</div>
                    </div>
                    {[
                      { init: 'OM', name: 'Oliver M.',  hrs: '38h', st: 'Approved', cls: 'lp-badge-g', bg: '#2563eb,#7c3aed' },
                      { init: 'EH', name: 'Emily H.',   hrs: '40h', st: 'Pending',  cls: 'lp-badge-a', bg: '#059669,#34d399' },
                      { init: 'JP', name: 'James P.',   hrs: '35h', st: 'Approved', cls: 'lp-badge-g', bg: '#db2777,#f472b6' },
                    ].map(r => (
                      <div className='lp-mock-tr' key={r.init}>
                        <div className='lp-mock-td'>
                          <div className='lp-mock-av' style={{ background: `linear-gradient(135deg,${r.bg})` }}>{r.init}</div>
                          {r.name}
                        </div>
                        <div className='lp-mock-num'>{r.hrs}</div>
                        <div className='lp-mock-num'><span className={`lp-mock-badge ${r.cls}`}>{r.st}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className='lp-mockup-float'>
                <div className='lp-float-pill'>
                  <span className='lp-fp-icon'>✅</span>
                  <div><div className='lp-fp-val'>12 reports</div><div className='lp-fp-lbl'>approved today</div></div>
                </div>
                <div className='lp-float-pill'>
                  <span className='lp-fp-icon'>💰</span>
                  <div><div className='lp-fp-val'>£2,340</div><div className='lp-fp-lbl'>expenses saved</div></div>
                </div>
              </div>
            </div>

          </div>
          <div className='lp-left-stripe' />
        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className='lp-right'>
          <div className='lp-form-card'>
            <div className='lp-card-stripe' />
            <div className='lp-card-body'>

              <div className='lp-greeting'>Welcome back 👋</div>
              <div className='lp-greeting-sub'>Sign in to access your TimeBerlo portal workspace.</div>

              {error && <div className='lp-err-banner'>{error}</div>}

              <form onSubmit={handleLogin}>

                {/* Email */}
                <div className={`lp-field${emailErr ? ' lp-error' : ''}`}>
                  <div className='lp-field-label'><span>Work email</span></div>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faEnvelope} /></span>
                    <input
                      className='lp-inp'
                      type='email'
                      placeholder='you@company.com'
                      autoComplete='email'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </div>
                  {emailErr && <div className='lp-field-error'>⚠ Please enter a valid email</div>}
                </div>

                {/* Password */}
                <div className={`lp-field${passErr ? ' lp-error' : ''}`}>
                  <div className='lp-field-label'>
                    <span>Password</span>
                    <Link to='/forgot'>Forgot password?</Link>
                  </div>
                  <div className='lp-inp-wrap'>
                    <span className='lp-inp-icon'><FontAwesomeIcon icon={faLock} /></span>
                    <input
                      className='lp-inp'
                      type={showPass ? 'text' : 'password'}
                      placeholder='Enter your password'
                      autoComplete='current-password'
                      style={{ paddingRight: '44px' }}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                    />
                    <button type='button' className='lp-inp-eye' onClick={() => setShowPass(p => !p)}>
                      <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {passErr && <div className='lp-field-error'>⚠ Password is required</div>}
                </div>

                {/* Remember me */}
                <div className='lp-remember-row'>
                  <div className={`lp-chk-box${rememberMe ? ' on' : ''}`} onClick={() => setRememberMe(r => !r)} />
                  <div className='lp-remember-lbl' onClick={() => setRememberMe(r => !r)}>Keep me signed in for 30 days</div>
                </div>

                <button type='submit' className='lp-btn-submit' disabled={loading}>
                  {loading
                    ? <><div className='lp-spinner' /><span>Signing in…</span></>
                    : <><span>Sign in to TimeBerlo</span><span className='lp-btn-arrow'>→</span></>
                  }
                </button>

              </form>

              <div className='lp-divider'>or continue with</div>
              <div className='lp-sso-row'>
                <button type='button' className='lp-sso-btn' disabled aria-label='Sign in with Microsoft (coming soon)'>
                  <svg width='18' height='18' viewBox='0 0 21 21' aria-hidden='true'><rect x='1' y='1' width='9' height='9' fill='#f25022'/><rect x='11' y='1' width='9' height='9' fill='#7fba00'/><rect x='1' y='11' width='9' height='9' fill='#00a4ef'/><rect x='11' y='11' width='9' height='9' fill='#ffb900'/></svg>
                </button>
                <button type='button' className='lp-sso-btn' disabled aria-label='Sign in with Google (coming soon)'>
                  <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'><path d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' fill='#4285F4'/><path d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' fill='#34A853'/><path d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z' fill='#FBBC05'/><path d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' fill='#EA4335'/></svg>
                </button>
                <button type='button' className='lp-sso-btn' disabled aria-label='Sign in with Facebook (coming soon)'>
                  <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'><path d='M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.514c-1.491 0-1.956.93-1.956 1.883v2.271h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z' fill='#1877F2'/></svg>
                </button>
              </div>
              <div className='lp-sso-soon'>Coming soon</div>

            </div>
          </div>

          <div className='lp-trust-row'>
            <div className='lp-trust-item'>🔒 <span>256-bit SSL</span></div>
            <div className='lp-trust-dot' />
            <div className='lp-trust-item'>🛡 <span>GDPR compliant</span></div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Login
